import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Smartphone, Download, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Install = () => {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setDeferredPrompt(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background/95 to-primary/5 py-12 px-4">
      <div className="container max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-primary to-primary/60 rounded-3xl flex items-center justify-center shadow-lg">
            <img src="/icon-192.png" alt="Her Season" className="w-24 h-24 rounded-2xl" />
          </div>
          <h1 className="font-heading text-4xl mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
            Install Her Season
          </h1>
          <p className="text-muted-foreground text-lg font-body">
            Get the full app experience right on your home screen
          </p>
        </div>

        <Card className="p-8 mb-6 bg-card/50 backdrop-blur">
          {isInstalled ? (
            <div className="text-center space-y-4">
              <CheckCircle2 className="w-16 h-16 mx-auto text-green-500" />
              <h2 className="font-heading text-2xl">Already Installed!</h2>
              <p className="text-muted-foreground font-body">
                Her Season is installed on your device. You can access it from your home screen.
              </p>
              <Button onClick={() => navigate("/")} className="mt-4">
                Go to App
              </Button>
            </div>
          ) : deferredPrompt ? (
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <Smartphone className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-heading text-xl mb-2">Install for Easy Access</h3>
                  <p className="text-muted-foreground font-body">
                    Add Her Season to your home screen for quick access, offline support, and a native app experience.
                  </p>
                </div>
              </div>
              
              <Button 
                onClick={handleInstall} 
                size="lg" 
                className="w-full gap-2"
              >
                <Download className="w-5 h-5" />
                Install Now
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <h3 className="font-heading text-xl">Manual Installation</h3>
              
              <div className="space-y-4 font-body text-sm">
                <div>
                  <h4 className="font-semibold mb-2">On iPhone/iPad (Safari):</h4>
                  <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
                    <li>Tap the <strong>Share</strong> button at the bottom</li>
                    <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                    <li>Tap <strong>Add</strong> to confirm</li>
                  </ol>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">On Android (Chrome):</h4>
                  <ol className="list-decimal pl-5 space-y-1 text-muted-foreground">
                    <li>Tap the <strong>menu</strong> (three dots)</li>
                    <li>Tap <strong>"Add to Home screen"</strong></li>
                    <li>Tap <strong>Add</strong> to confirm</li>
                  </ol>
                </div>
              </div>

              <Button onClick={() => navigate("/")} variant="outline" className="w-full">
                Continue in Browser
              </Button>
            </div>
          )}
        </Card>

        <div className="grid gap-4 md:grid-cols-3">
          <Card className="p-4 text-center bg-card/30 backdrop-blur">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <Smartphone className="w-6 h-6 text-primary" />
            </div>
            <h4 className="font-heading text-sm mb-1">Works Offline</h4>
            <p className="text-xs text-muted-foreground font-body">Access your data anytime</p>
          </Card>

          <Card className="p-4 text-center bg-card/30 backdrop-blur">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <CheckCircle2 className="w-6 h-6 text-primary" />
            </div>
            <h4 className="font-heading text-sm mb-1">Fast & Reliable</h4>
            <p className="text-xs text-muted-foreground font-body">Native app performance</p>
          </Card>

          <Card className="p-4 text-center bg-card/30 backdrop-blur">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <Download className="w-6 h-6 text-primary" />
            </div>
            <h4 className="font-heading text-sm mb-1">No App Store</h4>
            <p className="text-xs text-muted-foreground font-body">Install directly</p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Install;
